/**
 * KAN-980: таблица Telegram-аккаунтов пула (или всех кабинетов) с выбором строк.
 * Данные прокси и сессий сюда не приходят: только признак "прокси есть".
 */
import { FloodBadge } from '@/components/telegram/FloodBadge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { accountMarks, formatDateTime, isAccountSelectable } from '@/lib/platform';
import type { TgPoolAccount, TgPoolScope } from '@/services/api/platform';

interface TgPoolTableProps {
  accounts: TgPoolAccount[];
  scope: TgPoolScope;
  selected: ReadonlySet<string>;
  onToggle: (accountId: string) => void;
  /** KAN-997: снять пометку "Был под флудом" (кнопка только у помеченных строк). */
  onClearFlood?: (account: TgPoolAccount) => void;
}

const yesNo = (value: boolean): string => (value ? 'да' : 'нет');

export function TgPoolTable({ accounts, scope, selected, onToggle, onClearFlood }: TgPoolTableProps) {
  if (accounts.length === 0) {
    return <p className="text-sm text-gray-500">Аккаунтов нет</p>;
  }
  return (
    <div className="overflow-x-auto rounded-lg bg-white shadow">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8" />
            <TableHead>Телефон</TableHead>
            <TableHead>Метка</TableHead>
            <TableHead>Статус</TableHead>
            <TableHead>Подключён</TableHead>
            <TableHead>Возраст</TableHead>
            <TableHead>Продавец</TableHead>
            <TableHead>Прокси</TableHead>
            <TableHead>Рассылка</TableHead>
            <TableHead>Флаги</TableHead>
            <TableHead>Кабинет</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {accounts.map((acc) => {
            const selectable = isAccountSelectable(acc, scope);
            const marks = accountMarks(acc);
            return (
              <TableRow key={acc.account_id} data-testid="platform-pool-row">
                <TableCell>
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    aria-label={`Выбрать ${acc.phone ?? acc.account_id}`}
                    checked={selected.has(acc.account_id)}
                    disabled={!selectable}
                    onChange={() => onToggle(acc.account_id)}
                  />
                </TableCell>
                <TableCell className="whitespace-nowrap">{acc.phone ?? '-'}</TableCell>
                <TableCell>{acc.label ?? '-'}</TableCell>
                <TableCell className={acc.connected ? 'text-green-700' : 'text-gray-500'}>
                  {acc.connected ? 'Подключён' : 'Отключён'}
                </TableCell>
                <TableCell className="whitespace-nowrap">{formatDateTime(acc.connected_at)}</TableCell>
                <TableCell>{acc.age_days === null ? '-' : `${acc.age_days} дн.`}</TableCell>
                <TableCell>{acc.seller ?? '-'}</TableCell>
                <TableCell>{yesNo(acc.has_proxy)}</TableCell>
                <TableCell>{yesNo(acc.outreach)}</TableCell>
                <TableCell className="text-xs text-amber-700">
                  <div>{marks.length ? marks.join(', ') : '-'}</div>
                  <FloodBadge account={acc} />
                  {acc.flood_flagged && onClearFlood && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-1 block"
                      data-testid="platform-pool-clear-flood"
                      onClick={() => onClearFlood(acc)}
                    >
                      Снять пометку
                    </Button>
                  )}
                </TableCell>
                <TableCell>
                  <div>{acc.organization_name ?? '-'}</div>
                  {acc.operator_email && <div className="text-xs text-gray-500">{acc.operator_email}</div>}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
